import type { ProjectDefinition } from "../../../types";
import { type CellValue, type FuncionProjectSpec, conditions } from "../spec";

const SHEET = "TX Dividir";

const IN = {
  direccion: "Av. Constitución 1500, Col. Centro, Monterrey, Nuevo León, 64000",
  etiquetas: "Excel;Power BI;SQL;Python;Power Query",
  mixta: "Ventas,Finanzas;TI,Operaciones;Recursos Humanos",
  registros: "Ana,Ventas,25;Luis,TI,31;Marta,Finanzas,28;Jorge,Operaciones,35",
  productos: " Laptop ,  Mouse,Teclado  , Monitor ",
};
const PENDIENTES = ["Enviar cotización", "", "Llamar a proveedor", "Revisar inventario", "", "Pagar nómina"];

const EXPECTED: CellValue[][][] = [
  [IN.direccion.split(", ")],
  IN.etiquetas.split(";").map((t) => [t]),
  [IN.mixta.replace(/;/g, ",").split(",")],
  IN.registros.split(";").map((r) => r.split(",")),
  [[IN.direccion.split(", ").length]],
  [[PENDIENTES.filter((p) => p !== "").join(", ")]],
  [[IN.productos.split(",").map((p) => p.trim()).join(" | ")]],
];
const FNS = [
  ["TEXTSPLIT"], ["TEXTSPLIT"], ["TEXTSPLIT", "SUBSTITUTE"], ["TEXTSPLIT"], ["TEXTSPLIT", "COLUMNS|COUNTA"],
  ["TEXTJOIN"], ["TEXTJOIN", "TRIM", "TEXTSPLIT"],
];
const LABELS = [
  "Tarea 1 · Dirección en columnas",
  "Tarea 2 · Etiquetas hacia abajo",
  "Tarea 3 · Dos separadores distintos",
  "Tarea 4 · Texto a tabla",
  "Tarea 5 · ¿Cuántas partes?",
  "Tarea 6 · Pendientes en una línea",
  "Tarea 7 · Productos limpios",
];

const ANCHORS: string[] = [];
EXPECTED.reduce((row, result) => {
  ANCHORS.push(`B${row}`);
  return row + result.length + 3;
}, 12);

export const spec: FuncionProjectSpec = {
  projectId: "funciones-dividir",
  sheets: [
    {
      name: SHEET,
      title: "Separar y unir texto",
      subtitle: "Datos pegados en una sola celda — sepáralos o júntalos con una fórmula",
      tables: [{ name: "Pendientes", anchor: "J4", headers: ["Pendiente"], rows: PENDIENTES.map((p) => [p]) }],
      inputs: [
        { address: "B4", label: "Dirección", autofit: false, value: IN.direccion },
        { address: "B5", label: "Etiquetas", autofit: false, value: IN.etiquetas },
        { address: "B6", label: "Departamentos", autofit: false, value: IN.mixta },
        { address: "B7", label: "Registros", autofit: false, value: IN.registros },
        { address: "B8", label: "Productos", autofit: false, value: IN.productos },
      ],
      answers: ANCHORS.map((address, i) => ({ address, label: LABELS[i], labelPosition: "above" as const })),
    },
  ],
  checks: Object.fromEntries(
    EXPECTED.map((expected, i) => [
      i + 1,
      { kind: "spill" as const, sheet: SHEET, address: ANCHORS[i], fns: FNS[i], expected, exact: true },
    ])
  ),
};

const F = (text: string) => `<span class="formula">${text}</span>`;

export const project: ProjectDefinition = {
  id: "funciones-dividir",
  title: "DIVIDIRTEXTO y UNIRCADENAS: Separar y unir",
  subtitle: "De una celda a muchas, y de muchas a una",
  description:
    "Es muy común recibir datos pegados: una dirección completa en una celda, etiquetas separadas por punto y coma, registros enteros copiados de un sistema… o al revés, una lista que hay que convertir en una sola línea para un correo.\n\nDIVIDIRTEXTO separa un texto en columnas, filas o una tabla completa (y el resultado se derrama). UNIRCADENAS hace lo contrario: une una lista con el separador que elijas e ignora las celdas vacías.",
  topics: [
    "Separar en columnas y en filas",
    "Separadores distintos: SUSTITUIR primero",
    "Convertir un texto en tabla",
    "Contar partes",
    "UNIRCADENAS ignorando vacíos",
    "Separar, limpiar y volver a unir",
  ],
  tasks: [
    {
      id: 1,
      level: "basico",
      title: "Tarea 1: Dirección en columnas",
      description: `En la hoja <strong>TX Dividir</strong>, celda <strong>${ANCHORS[0]}</strong>: separa la dirección de <strong>B4</strong> en columnas usando <strong>coma y espacio</strong> como separador.`,
      solution: [
        `Selecciona ${F(ANCHORS[0])}.`,
        "DIVIDIRTEXTO(texto, separador_de_columnas). El resultado se derrama hacia la derecha.",
        `Escribe ${F('=DIVIDIRTEXTO(B4,", ")')}.`,
      ],
      expectedResult: "Av. Constitución 1500 | Col. Centro | Monterrey | Nuevo León | 64000",
      finalFormula: '=DIVIDIRTEXTO(B4,", ")',
    },
    {
      id: 2,
      level: "basico",
      title: "Tarea 2: Etiquetas hacia abajo",
      description: `En <strong>${ANCHORS[1]}</strong>: separa las etiquetas de <strong>B5</strong> (separadas por <strong>;</strong>) pero <strong>una debajo de otra</strong>.`,
      solution: [
        `Selecciona ${F(ANCHORS[1])}.`,
        "El segundo argumento separa en columnas y el tercero en filas. Deja vacío el segundo.",
        `Escribe ${F('=DIVIDIRTEXTO(B5,,";")')}.`,
      ],
      expectedResult: "Excel, Power BI, SQL, Python, Power Query hacia abajo",
      finalFormula: '=DIVIDIRTEXTO(B5,,";")',
    },
    {
      id: 3,
      level: "intermedio",
      title: "Tarea 3: Dos separadores distintos",
      description: conditions(`En <strong>${ANCHORS[2]}</strong>: los departamentos de <strong>B6</strong> están separados a veces por <strong>coma</strong> y a veces por <strong>punto y coma</strong>. Sepáralos todos en una fila:`, [
        "Primero convierte los <strong>;</strong> en <strong>,</strong> con SUSTITUIR",
        "Después separa por <strong>,</strong>",
      ]),
      solution: [
        `Selecciona ${F(ANCHORS[2])}.`,
        `Escribe ${F('=DIVIDIRTEXTO(SUSTITUIR(B6,";",","),",")')}.`,
      ],
      expectedResult: "Ventas | Finanzas | TI | Operaciones | Recursos Humanos",
      finalFormula: '=DIVIDIRTEXTO(SUSTITUIR(B6,";",","),",")',
    },
    {
      id: 4,
      level: "intermedio",
      title: "Tarea 4: De texto a tabla",
      description: conditions(`En <strong>${ANCHORS[3]}</strong>: <strong>B7</strong> trae varios registros en una sola celda. Conviértelos en una tabla de 3 columnas:`, [
        "La <strong>coma</strong> separa columnas (nombre, área, edad)",
        "El <strong>punto y coma</strong> separa filas (una persona por fila)",
      ]),
      solution: [
        `Selecciona ${F(ANCHORS[3])}.`,
        `Usa los dos separadores a la vez: ${F('","')} para columnas y ${F('";"')} para filas.`,
        `Escribe ${F('=DIVIDIRTEXTO(B7,",",";")')}.`,
      ],
      expectedResult: "Una tabla de 4 filas × 3 columnas",
      finalFormula: '=DIVIDIRTEXTO(B7,",",";")',
    },
    {
      id: 5,
      level: "intermedio",
      title: "Tarea 5: ¿Cuántas partes tiene?",
      description: `En <strong>${ANCHORS[4]}</strong>: devuelve <strong>cuántas partes</strong> tiene la dirección de B4 al separarla por \", \" (un número).`,
      solution: [
        `Selecciona ${F(ANCHORS[4])}.`,
        "COLUMNAS cuenta cuántas columnas tiene el resultado de DIVIDIRTEXTO.",
        `Escribe ${F('=COLUMNAS(DIVIDIRTEXTO(B4,", "))')}.`,
      ],
      expectedResult: "5",
      finalFormula: '=COLUMNAS(DIVIDIRTEXTO(B4,", "))',
    },
    {
      id: 6,
      level: "reto",
      title: "Tarea 6: Pendientes en una sola línea",
      description: conditions(`En <strong>${ANCHORS[5]}</strong>: une los pendientes de la tabla <strong>Pendientes</strong> (J4) en un solo texto:`, [
        "Separados por <strong>coma y espacio</strong>",
        "<strong>Sin</strong> las celdas vacías (no deben quedar \", ,\")",
      ]),
      solution: [
        `Selecciona ${F(ANCHORS[5])}.`,
        `UNIRCADENAS(separador, ignorar_vacías, rango). Con ${F("VERDADERO")} se salta las celdas en blanco.`,
        `Escribe ${F('=UNIRCADENAS(", ",VERDADERO,Pendientes[Pendiente])')}.`,
      ],
      expectedResult: "Enviar cotización, Llamar a proveedor, Revisar inventario, Pagar nómina",
      finalFormula: '=UNIRCADENAS(", ",VERDADERO,Pendientes[Pendiente])',
    },
    {
      id: 7,
      level: "reto",
      title: "Tarea 7: Separar, limpiar y unir",
      description: conditions(`En <strong>${ANCHORS[6]}</strong>: la lista de productos de <strong>B8</strong> tiene espacios de más alrededor de cada nombre. Devuelve un solo texto limpio:`, [
        "Separa por coma con <strong>DIVIDIRTEXTO</strong>",
        "Quita los espacios de cada parte con <strong>ESPACIOS</strong> (funciona sobre todo el resultado a la vez)",
        "Une con <strong>\" | \"</strong> usando <strong>UNIRCADENAS</strong>",
      ]),
      solution: [
        `Selecciona ${F(ANCHORS[6])}.`,
        `Escribe ${F('=UNIRCADENAS(" | ",VERDADERO,ESPACIOS(DIVIDIRTEXTO(B8,",")))')}.`,
      ],
      expectedResult: "Laptop | Mouse | Teclado | Monitor",
      finalFormula: '=UNIRCADENAS(" | ",VERDADERO,ESPACIOS(DIVIDIRTEXTO(B8,",")))',
    },
  ],
};
